import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import { Truck, Wrench, Package, Zap, Users, CheckCircle } from 'lucide-react';
import { applyStyle } from '@/utils/styleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultServices = [
  { icon: 'Package', title: 'Custom Design & Engineering', description: 'Our design team works with you to create packaging that perfectly fits your product specifications and brand identity.', features: ['3D prototyping and samples', 'Structural design optimization', 'Brand integration and graphics', 'Size and material consultation'] },
  { icon: 'Wrench', title: 'Manufacturing Excellence', description: 'State-of-the-art production facilities equipped with modern machinery for high-volume, precision manufacturing.', features: ['Automated production lines', 'Real-time quality monitoring', 'Flexible batch sizes', 'Fast turnaround times'] },
  { icon: 'Truck', title: 'Logistics & Distribution', description: 'Comprehensive delivery solutions ensuring your packaging arrives safely and on schedule.', features: ['Nationwide delivery network', 'Just-in-time delivery options', 'Warehousing solutions', 'Track and trace capabilities'] },
  { icon: 'Zap', title: 'Rapid Prototyping', description: 'Quick sample production to validate designs before committing to large production runs.', features: ['Digital mockups in 24 hours', 'Physical samples in 3-5 days', 'Multiple iterations supported', 'No setup fees for samples'] },
  { icon: 'Users', title: 'Account Management', description: 'Dedicated support team to manage your orders, answer questions, and ensure complete satisfaction.', features: ['Personal account manager', '24/7 customer support', 'Online order tracking', 'Flexible payment terms'] },
  { icon: 'CheckCircle', title: 'Quality Assurance', description: 'Rigorous testing and inspection processes to ensure every box meets our high standards.', features: ['ISO-certified processes', 'Compression testing', 'Drop and impact testing', 'Detailed quality reports'] },
];

const defaultContent = {
  hero: {
    title: 'Our Services',
    subtitle: 'Comprehensive packaging solutions from concept to delivery',
    backgroundImage: 'https://images.pexels.com/photos/34718922/pexels-photo-34718922.jpeg',
  },
  grid: {
    badge: 'WHAT WE OFFER',
    title: 'Complete Packaging Solutions',
    subtitle: 'End-to-end services designed to make your packaging procurement seamless',
  },
  services: { items: defaultServices },
};

const iconMap = { Package, Wrench, Truck, Zap, Users, CheckCircle };

const heroFields = [
  { key: 'title', label: 'Title', type: 'title', styled: true },
  { key: 'subtitle', label: 'Subtitle', type: 'text', styled: true },
  { key: 'backgroundImage', label: 'Background Image', type: 'image' },
];

const gridFields = [
  { key: 'badge', label: 'Badge', type: 'text', styled: true },
  { key: 'title', label: 'Title', type: 'title', styled: true },
  { key: 'subtitle', label: 'Subtitle', type: 'text', multiline: true, styled: true },
];

const servicesFields = [
  { key: 'items', label: 'Services', type: 'list', itemFields: [
    { key: 'icon', label: 'Icon', type: 'text' },
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
  ]},
];

export default function ServicesPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/services`);
        if (r.data?.content) setContent({ ...defaultContent, ...r.data.content });
      } catch (error) { console.error('Failed to load services content:', error); }
    })();
  }, []);

  const updateSection = (key) => (data) => setContent(prev => ({ ...prev, [key]: data }));
  const services = content.services?.items || defaultServices;

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      <section className="relative h-[55vh] flex items-center justify-center group">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: `url(${content.hero?.backgroundImage || defaultContent.hero.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(2,20,10,0.75) 50%, rgba(0,0,0,0.7) 100%)' }}></div>
        </div>
        <SectionEditor user={user} sectionTitle="Hero" fields={heroFields} content={content.hero} pageName="services" sectionKey="hero" onSave={updateSection('hero')} />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tighter" style={applyStyle(content.hero?.titleStyle)} data-testid="services-title">{content.hero?.title || 'Our Services'}</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto" style={applyStyle(content.hero?.subtitleStyle)}>{content.hero?.subtitle || 'Comprehensive packaging solutions from concept to delivery'}</p>
        </div>
      </section>

      <section className="py-24 relative section-fade-top group">
        <SectionEditor user={user} sectionTitle="Services Grid" fields={gridFields} content={content.grid} pageName="services" sectionKey="grid" onSave={updateSection('grid')} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge mb-4" style={applyStyle(content.grid?.badgeStyle)}>{content.grid?.badge || 'WHAT WE OFFER'}</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight" style={applyStyle(content.grid?.titleStyle)}>{content.grid?.title || 'Complete Packaging Solutions'}</h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={applyStyle(content.grid?.subtitleStyle)}>{content.grid?.subtitle || ''}</p>
          </div>
          <div className="relative group">
            <SectionEditor user={user} sectionTitle="Service Cards" fields={servicesFields} content={content.services || { items: defaultServices }} pageName="services" sectionKey="services" onSave={updateSection('services')} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Package;
              return (
                <motion.div key={service.title || `service-${index}`} className="feature-card" data-testid={`service-card-${index}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }}>
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6"><Icon className="w-7 h-7 text-primary" /></div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{service.title}</h3>
                  <p className="text-muted-foreground mb-6 text-sm">{service.description}</p>
                  <ul className="space-y-2">
                    {(service.features || []).map((f, idx) => (
                      <li key={idx} className="flex items-start text-sm text-foreground/80">
                        <CheckCircle className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
